import { useEffect, useState } from "react";

const myDomain = window && window.location ? window.location.protocol + "//" + window.location.host : "http://localhost:3000";

export interface ShowLockOptions {
    /**
     * Domain of the authorization server.  Defaults to __AUTH_DOMAIN__.
     *
     * @type {string}
     * @memberof ShowLockOptions
     */
    domain?: string;
    /**
     * Client ID of the authorization server.  Defaults to __AUTH_CLIENT_ID__
     *
     * @type {string}
     * @memberof ShowLockOptions
     */
    clientID?: string;
    /**
     * The URL to redirect to.  Defaults to `<current_domain>/authorize`
     *
     * @type {string}
     * @memberof ShowLockOptions
     */
    redirectUri?: string;
    /**
     * Auth0 Audience to authorize for.  Defaults to __AUTH_AUDIENCE__
     *
     * @type {string}
     * @memberof ShowLockOptions
     */
    audience?: string;
    /**
     * The auth0 page to show.  Will either be "sign up" or "login".  Defaults to "login".
     *
     * The user can still "sign up" if the mode is in login. This is just the initial page thatis shown.
     *
     * @type {string}
     * @memberof ShowLockOptions
     */
    mode?: string;
    /**
     * The authentication provider that is used.  Defaults to __AUTH_PROVIDER__
     *
     * @type {("auth0" | "cognito")}
     * @memberof ShowLockOptions
     */
    authProvider?: "auth0" | "cognito";
    /**
     * A string to provide as a "state".  The string can be encoded or a random string. It will be returned
     * at the redirected URI in either the hash or search parameter depending on whether the response type is "token" or
     * "code" respectively.  The state returned must be compared with the state provided and an error thrown if they differ.
     *
     * @type {string}
     * @memberof ShowLockOptions
     */
    state?: string;
    /**
     * If doing a "code" response, the code challenge to give the authentication provided.
     *
     * When the code is retrieved, the "code_verifier" must be uploaded to the auth provider when
     * requesting a token. The "code_challenge" must be an encrypted and base64 encoded version of the
     * code_verifier
     *
     * @type {{
     *         codeChallenge: string;
     *         codeChallengeMethod: string;
     *     }}
     * @memberof ShowLockOptions
     */
    codeChallenge?: {
        codeChallenge: string;
        codeChallengeMethod: string;
    };
}

/**
 * Redirects the user to an an authentication page which will redirect back to this website once the user is authorized.
 *
 * If a Code Challenge is provided, the response type will be "code".
 * @see https://auth0.com/docs/flows/guides/auth-code-pkce/call-api-auth-code-pkce
 *
 * If a Code Challenge is not provided, the response type will be a "token".  The token will be provided in the hash of the URL
 * of the URL that is redirected.
 * @see https://auth0.com/docs/flows/guides/implicit/call-api-implicit
 *
 * @export
 * @param {ShowLockOptions} [options={}]
 */
export function* showLock(options: ShowLockOptions = {}) {
    const url = buildWebAuthUrl(options);
    window.location.replace(url);
    // yield put(replace(url));
}

export function buildWebAuthUrl(options: ShowLockOptions = {}): string {
    const {
        authProvider = window.xaStudioConfig?.auth.provider, //__AUTH_PROVIDER__,
        domain = window.xaStudioConfig?.auth?.domain, // __AUTH_DOMAIN__,
        clientID = window.xaStudioConfig?.auth?.clientId, //__AUTH_CLIENT_ID__,
        audience = window.xaStudioConfig?.auth?.audience, // __AUTH_AUDIENCE_API__,
        redirectUri = `${myDomain}/authorize`,
        mode = options.mode, // "login",
        codeChallenge,
        state
    } = options;

    const responseType = (codeChallenge) ? "code" : "token";

    let url = `https://${domain}/`;
    if (authProvider.toLocaleLowerCase() === "cognito") {
        url += `${mode || "login"}` +
            `?scope=email+openid`;
    } else {
        // auth0
        url += `authorize` +
            `?protocol=oauth2` +
            `&prompt=login` +
            `&mode=${mode || "login"}` +
            `&audience=${audience}` +
            `&scope=email+openid+profile+offline_access`;

        if (mode === "signup") {
            url += `&screen_hint=signup`
        }
    }

    url += `&response_type=${responseType}` +
        `&client_id=${clientID}` +
        `&redirect_uri=${redirectUri}`;

    if (state) {
        url += `&state=${state}`;
    }
    if (codeChallenge) {
        url += `&code_challenge=${codeChallenge.codeChallenge}` +
            `&code_challenge_method=${codeChallenge.codeChallengeMethod}`;
    }
    return encodeURI(url);
}

export enum LockType {
    Login,
    SignUp,
    LinkAccount
}

export type ScreenType = "login" | "signUp";

export function getInitialScreen(lockType: LockType = LockType.Login): ScreenType {
    switch (lockType) {
        case LockType.SignUp:
            return "signUp";
        default:
            return undefined;
    }
}

export interface CheckSessionResult {
    accessToken: string;
    expiresIn: number;
}

export interface GetAccessTokenProps {
    code: string;
    codeVerifier: string;
    domain?: string;
    clientID?: string;
    redirectUri?: string;
    authProvider?: "auth0" | "cognito";
}

export interface TokenRaw {
    access_token: string;
    refresh_token?: string;
    expires_in: number;
    id_token: string;
    token_type: string;
}

export interface Token {
    accessToken: string;
    idToken: string;
    refreshToken?: string;
    expiresIn: number;
    tokenType: string;
}

/**
 * Returns an access token.
 *
 * It will redirect to the /authorize endpoint with appropriate code and code_verifier.
 * @param options
 * @returns
 */
export function getAccessToken(options: GetAccessTokenProps): Promise<TokenRaw> {

    const {
        code,
        authProvider = window.xaStudioConfig?.auth.provider, //__AUTH_PROVIDER__,
        domain = window.xaStudioConfig?.auth?.domain, // __AUTH_DOMAIN__,
        clientID = window.xaStudioConfig?.auth?.clientId, // __AUTH_CLIENT_ID__,
        redirectUri = `${myDomain}/authorize`,
        codeVerifier,
    } = options;

    const body = `client_id=${clientID}` +
        `&grant_type=authorization_code` +
        `&code_verifier=${codeVerifier}` +
        `&code=${code}` +
        `&redirect_uri=${redirectUri}`;

    const endpoint = (authProvider.toLocaleLowerCase() === "cognito") ? "oauth2/token" : "oauth/token";

    let status: number;

    return fetch(`https://${domain}/${endpoint}`, {
        body,
        headers: { "content-type": "application/x-www-form-urlencoded" },
        method: "POST",
    }).then((result) => {

        status = result.status;

        return result.json();

    }).then((body) => {
        if (status > 200) {
            throw new Error(`There was an error fetching the token: ${body?.error} ${body?.error_description}`);
        } else {
            return body;
        }
    });
}

export interface UseAccessTokenProps extends GetAccessTokenProps {
    pause?: boolean;
}

/**
 * Returns an access token based on the provided code.
 *
 * @param options
 * @returns
 */
export function useAccessToken(options: UseAccessTokenProps): { token: Token, error: Error, fetching: boolean } {

    const [token, setAccessToken] = useState<Token>(null);
    const [error, setError] = useState<Error>(null);
    const [fetching, setFetching] = useState<boolean>(false);

    useEffect(() => {
        async function fetchToken() {
            try {
                setFetching(true);
                const accessToken = await getAccessToken(options);
                setAccessToken({
                    accessToken: accessToken.access_token,
                    expiresIn: accessToken.expires_in,
                    idToken: accessToken.id_token,
                    refreshToken: accessToken.refresh_token,
                    tokenType: accessToken.token_type
                });
            } catch (err) {
                setError(err);
            } finally {
                setFetching(false);
            }
        }

        if (!options.pause && options.code && !token && !error && !fetching) {
            fetchToken();
        }
    }, [error, fetching, options, token]);

    return { token, error, fetching };
}

export interface RefreshAccessTokenProps {
    authProvider?: string;
    domain?: string;
    clientID?: string;
    refreshToken: string;
}

export type RefreshTokenReturn = TokenRaw & { refresh_token: string };

export function refreshAccessToken(options: RefreshAccessTokenProps): Promise<RefreshTokenReturn> {
    const {
        authProvider = window.xaStudioConfig?.auth.provider, // __AUTH_PROVIDER__,
        domain = window.xaStudioConfig?.auth?.domain, //__AUTH_DOMAIN__,
        clientID = window.xaStudioConfig?.auth?.clientId, // __AUTH_CLIENT_ID__,
        refreshToken
    } = options;
    const body = `client_id=${clientID}` +
        `&refresh_token=${refreshToken}` +
        `&grant_type=refresh_token`;
    const endpoint = (authProvider.toLocaleLowerCase() === "cognito") ? "oauth2/token" : "oauth/token";
    return fetch(`https://${domain}/${endpoint}`, {
        body,
        headers: { "content-type": "application/x-www-form-urlencoded" },
        method: "POST",
    }).then((result) => {
        return result.json().then((body) => {
            if (result.status >= 200 && result.status < 300) {
                return body;
            }
            throw new Error("There was an error authorizing at this time.");
        });
    }).then((returnValue) => ({
        // Some auth providers use rotating refresh tokens in which case we'll use that one.
        // Otherwise, the old refresh token is still useful.
        ...returnValue,
        refresh_token: returnValue.refresh_token || refreshToken
    }));
}
